import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import parse from 'html-react-parser'
import React from 'react'

import Anchors from '../components/Anchors'
import Body from '../components/Body'
import { Button } from '../components/Button'
import CTABookADemo from '../components/ctas/CTABookADemo'
import CTAMain, { ButtonBlock } from '../components/ctas/CTAMain'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Grid from '../components/Grid'
import Header from '../components/Header'
import { HippoVideo } from '../components/HippoVideo'
import { HubspotForm } from '../components/HubspotForm'
import HubspotSubmittedBlock from '../components/HubspotSubmittedBlock'
import Image from '../components/Image'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import ReadingProgressBar from '../components/ReadingProgressBar'
import RichText from '../components/RichText'
import { RichTextMarkdown } from '../components/RichTextMarkdown'
import { SearchResultsBlock } from '../components/Search'
import SEO from '../components/SEO'
import SocialSharing from '../components/SocialSharing'
import TextCard from '../components/TextCard'
import VerticalTextDivider from '../components/VerticalTextDivider'
import VideoComponent from '../components/VideoComponent'
import { breakpoints, headerWithSearchBreakPoint } from '../constants/breakPoints'
import { longCopyBreakPoint } from '../constants/breakPoints'
import { getDateDayAppendix, isMobile } from '../helpers/common'
import { getAnchors, richTextRenderingOptions } from '../helpers/contentful'
import { getCtaMainType } from '../helpers/cta'
import { getSearchHeaderEntriesLogin } from '../helpers/header'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { EventProps } from '../interfaces/events'
import { anchorsFallbackStyle } from './blog/BlogPost'
import { filteredTextCardStyle } from './resources/EducationCategory'
import { longCopyBlockStyle } from './resources/GuidePage'

const Event: React.FC<EventProps> = ({
  data: {
    event: {
      id,
      isPast,
      title,
      type,
      cardTitle,
      cardDescription,
      externalRegistrationLink,
      fullDate,
      description: { description },
      hubspotFormId,
      imageWithBorder,
      hippoVideoId,
      eventCategory,
      eventInfoItems,
      eventSpeakers,
      airfolks,
      eventSummary,
      banner,
      heroImage,
    },
    pastEventsData: { edges: pastEvents },
    upcomingEventsData: { edges: upcomingEvents },
  },
}) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const currentEventCategory = eventCategory
  const moreWebinars = [
    ...upcomingEvents,
    ...pastEvents.filter(({ node: { eventCategory } }) => eventCategory === currentEventCategory),
    ...pastEvents.filter(({ node: { eventCategory } }) => eventCategory !== currentEventCategory),
  ]

  const isConferencePage = type === 'Conference'

  const json = eventSummary ? React.useMemo(() => JSON.parse(eventSummary.raw), [eventSummary]) : null
  const anchors = json ? React.useMemo(() => getAnchors(json), [eventSummary]) : null

  const [speakersHeight, setSpeakersHeight] = React.useState(0)
  // avatarHeight only needs to be calculated for 3 columns (sidebar visible)
  if (eventSummary && !isMobile(longCopyBreakPoint)) {
    React.useEffect(() => {
      setSpeakersHeight(document.getElementById('event-summary-speaker-block')!.clientHeight)
    }, [])
  }

  // filter Events by (debounced) search term
  const filteredEvents = React.useMemo(
    () =>
      moreWebinars.filter(({ node: { cardTitle } }) => cardTitle.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch]
  )

  return (
    <Layout whiteBG>
      <SEO
        title={`${type}: ${cardTitle}`}
        lang="en"
        description={cardDescription}
        imageSrc={`https:${banner.file.url}`}
      />
      <Header
        search={search}
        setSearch={setSearch}
        entriesLogin={getSearchHeaderEntriesLogin}
        customBreakPoint={headerWithSearchBreakPoint}
        searchPlaceholder="Search events..."
        hideOnScroll={!!eventSummary}
      />
      <Body>
        {eventSummary && <ReadingProgressBar />}
        {isPast ? (
          <div className="full-screen-relative" css={pastHeroStyle(imageWithBorder)}>
            {hippoVideoId && (
              <div css={hippoVideoStyle}>
                <HippoVideo hippoVideoId={hippoVideoId} />
              </div>
            )}
          </div>
        ) : (
          <>
            <Grid
              columns={2}
              breakPoint={isConferencePage ? breakpoints.large : breakpoints.medium}
              cssStyles={upcomingHeroStyle(isConferencePage)}
            >
              <div>
                <div className="strong-body-1 bold blue-text">Upcoming</div>
                <div className="subtitle blue-text" css={upcomingDateStyle}>
                  {fullDate}
                </div>
                <h1 className={`heading2 ${isConferencePage && 'no-margin'}`}>{title}</h1>
                {eventInfoItems ? (
                  <>
                    <div className="subtitle">Join the {type.toLowerCase()} and learn:</div>
                    <ul className="subtitle long-copy" css={listStyle}>
                      {eventInfoItems.map((item, index) => (
                        <li key={`event-info-item-${index}`} className="subtitle long-copy no-margin ">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <div className="subtitle">{cardDescription}</div>
                    <ButtonBlock
                      primaryButton={
                        <Button type="primary" link={externalRegistrationLink}>
                          <p className="strong-body-2">Secure your spot</p>
                        </Button>
                      }
                      ghostButton={
                        <Button type="ghost" className="black" link="https://airfocus.com/get-in-touch/">
                          <p className="strong-body-2">Connect with us</p>
                        </Button>
                      }
                    />
                  </>
                )}
              </div>
              {hubspotFormId && (
                <HubspotForm
                  withGrayBackground
                  hubspotFormId={hubspotFormId}
                  submittedBlock={<HubspotSubmittedBlock />}
                />
              )}
              {heroImage && (
                <div css={heroImageStyle}>
                  <GatsbyImage image={heroImage.gatsbyImageData} alt={title} />
                </div>
              )}
            </Grid>
            <Divider size={8} isTop={false} />
          </>
        )}

        {isPast && (
          <div css={pastIntroStyle}>
            <Link href="/resources/events#past-events/">
              <h4 className="heading4 blue-text center opacity-on-hover">{eventCategory}</h4>
            </Link>
            <h1 className="heading2 center no-margin">{parse(title)}</h1>
            <div className="subtitle center no-margin">On-demand</div>
          </div>
        )}

        {eventSummary ? (
          <Grid cssStyles={longCopyBlockStyle}>
            {!anchors || anchors.length === 0 ? (
              <h3 className="heading3 no-margin hide-on-small-medium" css={anchorsFallbackStyle}>
                {title}
              </h3>
            ) : (
              <Anchors anchors={anchors} />
            )}
            <div>
              <RichText>{renderRichText(eventSummary, richTextRenderingOptions)}</RichText>
              <Divider size={2} />
              <SocialSharing title={title} />
            </div>

            {!isMobile(longCopyBreakPoint) && (
              <div css={sidebarStyle(speakersHeight)}>
                <div id="event-summary-speaker-block">
                  <h3 className="heading3">Our speaker{eventSpeakers.length > 1 ? 's' : ''}</h3>
                  {eventSpeakers.map(({ avatar, name, position, linkedIn }, index) => (
                    <>
                      <Grid
                        key={`event-summary-speaker-${name}`}
                        alignContent="flex-start"
                        rowGap={0.75}
                        noBreak
                        cssStyles={upcomingSpeakerBlockStyle(index)}
                      >
                        {linkedIn ? (
                          <Link href={linkedIn} css={summarySpeakerImageStyle}>
                            <GatsbyImage
                              image={avatar.gatsbyImageData}
                              alt={name}
                              imgStyle={{ width: '4rem', height: '4rem', borderRadius: '50%' }}
                            />
                            <Link
                              className="linked-in-icon"
                              href={linkedIn}
                              css={linkedInIconStyle(isPast, !!eventSummary)}
                            >
                              <Image
                                src="events/linkedIn-icon.webp"
                                alt="linkedIn icon"
                                imgStyle={{ width: '1.5rem', height: '1.5rem' }}
                              />
                            </Link>
                          </Link>
                        ) : (
                          <GatsbyImage
                            image={avatar.gatsbyImageData}
                            alt={name}
                            imgStyle={{ width: '4rem', height: '4rem', borderRadius: '50%' }}
                          />
                        )}
                        <div>
                          <div className="strong-body-1">{name}</div>
                          <div className="body-2 no-margin">{position}</div>
                        </div>
                      </Grid>
                      {index !== eventSpeakers.length - 1 && <Divider size={1} notDecreased />}
                    </>
                  ))}
                </div>
                <Divider size={6.5} />
                <CTABookADemo isSidebarCta />
              </div>
            )}
          </Grid>
        ) : (
          <Grid
            columnsTemplate="7fr 5fr"
            alignContent="flex-start"
            columnGap={4}
            rowGap={isPast ? 3 : 0}
            cssStyles={detailsBlockStyle(isPast)}
          >
            <div>
              <h3 className="heading3">About the {type.toLowerCase()}</h3>
              <div className="subtitle long-copy">
                <RichTextMarkdown content={description} />
              </div>
            </div>
            {isPast ? (
              <div>
                <h3 className="heading3">What we covered:</h3>
                <ul className="subtitle long-copy" css={listStyle}>
                  {eventInfoItems.map((item, index) => (
                    <li key={`event-info-item-${index}`} className="subtitle long-copy no-margin">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div>
                {isMobile() && <Divider size={8} />}
                {eventSpeakers && (
                  <>
                    <h3 className="heading3">Meet our speaker{eventSpeakers.length > 1 ? 's' : ''}</h3>
                    {eventSpeakers.map(({ avatar, name, position, linkedIn }, index) => (
                      <>
                        <Grid
                          key={`event-speaker-${name}`}
                          noBreak
                          columnsTemplate="auto 1fr auto"
                          alignContent="flex-start"
                          cssStyles={upcomingSpeakerBlockStyle(index)}
                        >
                          <GatsbyImage
                            image={avatar.gatsbyImageData}
                            alt={name}
                            imgStyle={{ width: '4rem', height: '4rem', borderRadius: '50%' }}
                          />
                          <div>
                            <div className="strong-body-1">{name}</div>
                            <div className="body-1 no-margin">{position}</div>
                          </div>
                          {linkedIn && (
                            <Link
                              className="linked-in-icon opacity-on-hover"
                              href={linkedIn}
                              css={linkedInIconStyle(isPast, !!eventSummary)}
                            >
                              <Image
                                src="events/linkedIn-icon.webp"
                                alt="linkedIn icon"
                                imgStyle={{ width: '1.5rem', height: '1.5rem' }}
                              />
                            </Link>
                          )}
                        </Grid>
                        {index !== eventSpeakers.length - 1 && <Divider size={1} notDecreased />}
                      </>
                    ))}
                  </>
                )}
                {airfolks && (
                  <>
                    <h3 className="heading3">Meet us there</h3>
                    {airfolks.map(({ firstName, role, businessPicture, linkedIn }, index) => (
                      <>
                        <Grid
                          key={`event-speaker-${firstName}`}
                          noBreak
                          columnsTemplate="auto 1fr auto"
                          alignContent="flex-start"
                          cssStyles={upcomingSpeakerBlockStyle(index)}
                        >
                          <GatsbyImage
                            image={businessPicture.gatsbyImageData}
                            alt={firstName}
                            imgStyle={{ width: '4rem', height: '4rem', borderRadius: '50%' }}
                          />
                          <div>
                            <div className="strong-body-1">{firstName}</div>
                            <div className="body-1 no-margin">{role}</div>
                          </div>
                          {linkedIn && (
                            <Link
                              className="linked-in-icon opacity-on-hover"
                              href={linkedIn}
                              css={linkedInIconStyle(isPast, !!eventSummary)}
                            >
                              <Image
                                src="events/linkedIn-icon.webp"
                                alt="linkedIn icon"
                                imgStyle={{ width: '1.5rem', height: '1.5rem' }}
                              />
                            </Link>
                          )}
                        </Grid>
                        {index !== airfolks.length - 1 && <Divider size={1} notDecreased />}
                      </>
                    ))}
                  </>
                )}
              </div>
            )}
          </Grid>
        )}

        <Divider size={8} />

        {isPast && !eventSummary && (
          <>
            <h2 className="heading2 center">Meet our speaker{eventSpeakers.length > 1 ? 's' : ''}</h2>
            {eventSpeakers.map(({ name, avatar, description: { description }, linkedIn }, index) => {
              const imageOnLeft = (index + 1) % 2 === 0
              return (
                <Grid
                  key={`event-speaker-${name}`}
                  columns={2}
                  columnsTemplate={imageOnLeft ? '180px auto' : 'auto 180px'}
                  noBreakForTablet
                  maxWidth="750px"
                  responsiveReversed={!imageOnLeft}
                  breakPoint={breakpoints.small}
                  cssStyles={speakersContainerStyle}
                >
                  {!imageOnLeft && (
                    <div className="subtitle long-copy" css={speakerDescriptionStyle}>
                      {parse(description)}
                    </div>
                  )}
                  {!!linkedIn ? (
                    <div>
                      <div className="center" css={speakerImageStyle}>
                        <Link href={linkedIn}>
                          <GatsbyImage
                            image={avatar.gatsbyImageData}
                            alt={name}
                            imgStyle={{ width: '8rem', height: '8rem', borderRadius: '50%' }}
                          />
                          <div className="linked-in-icon" css={linkedInIconStyle(isPast, !!eventSummary)}>
                            <Image
                              src="events/linkedIn-icon.webp"
                              alt="linkedIn icon"
                              imgStyle={{ width: '2rem', height: '2rem' }}
                            />
                          </div>
                        </Link>
                      </div>
                      <div>
                        <div className="strong-body-1 center">{name}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="center" css={speakerImageStyle}>
                      <GatsbyImage
                        image={avatar.gatsbyImageData}
                        alt={name}
                        imgStyle={{ width: '8rem', height: '8rem', borderRadius: '50%' }}
                      />
                    </div>
                  )}
                  {imageOnLeft && (
                    <div className="subtitle long-copy" css={speakerDescriptionStyle}>
                      {parse(description)}
                    </div>
                  )}
                </Grid>
              )
            })}
            <Divider size={8} />
          </>
        )}

        <div css={moreWebinarsStyle}>
          <h2 className="heading2">More events</h2>
          <Grid columns={3} rowGap={2} twoColumnsForTablet alignContent="stretch">
            {moreWebinars
              // filter out current event (current LP)
              .filter((event) => event.node.id !== id)
              // limit to 4 events
              .slice(0, 4)
              .map(
                (
                  {
                    node: {
                      isPast,
                      cardTitle,
                      cardDescription,
                      banner,
                      imageWithBorder,
                      hippoVideoId,
                      withoutLandingPage,
                      slug,
                      date,
                      type,
                      shortDate,
                      externalRegistrationLink,
                    },
                  },
                  index
                ) => {
                  const pastDay = dayjs(date).format('DD')
                  const pastDayAppendix = getDateDayAppendix(pastDay)
                  const pastDate = `${dayjs(date).format('MMM')} ${pastDay}${pastDayAppendix} ${dayjs(date).format(
                    'YYYY'
                  )}`
                  return (
                    <>
                      {!!isPast ? (
                        hippoVideoId ? (
                          <VideoComponent
                            key={cardTitle}
                            className={index === 3 ? 'hide-on-small hide-on-large-desktop' : undefined}
                            blueSubtitle={type}
                            graySubtitle={`Rec. ${pastDate}`}
                            video={{
                              title: cardTitle,
                              desc: cardDescription,
                              gatsbyImageSrc: banner,
                              imgWithBorder: imageWithBorder,
                              hippoVideoId,
                              showTitle: true,
                              hippoLink: !withoutLandingPage ? `/resources/events/${slug}/` : undefined,
                              hippoLinkText: !withoutLandingPage ? 'Learn more' : undefined,
                              linkText: !withoutLandingPage ? 'Learn more' : 'Watch video',
                              arrowLinkUrl: !withoutLandingPage ? `/resources/events/${slug}/` : undefined,
                            }}
                            withPlayButton
                            withImageOverlay
                          />
                        ) : (
                          <TextCard
                            key={cardTitle}
                            blueSubtitle={type}
                            graySubtitle={`${dayjs(date).format('MMM')} ${pastDay}${pastDayAppendix} ${dayjs(
                              date
                            ).format('YYYY')}`}
                            title={cardTitle}
                            description={cardDescription}
                            gatsbyImageSrc={banner}
                            imgWithBorder={imageWithBorder}
                            linkText="Learn more"
                            linkUrl={`/resources/events/${slug}/`}
                          />
                        )
                      ) : (
                        <TextCard
                          key={slug}
                          className={index === 3 ? 'hide-on-small hide-on-large-desktop' : undefined}
                          blueSubtitle={type}
                          graySubtitle={shortDate}
                          title={cardTitle}
                          description={cardDescription}
                          gatsbyImageSrc={banner}
                          imgWithBorder={imageWithBorder}
                          linkText="Sign up here"
                          linkUrl={externalRegistrationLink ? externalRegistrationLink : `/resources/events/${slug}/`}
                        />
                      )}
                    </>
                  )
                }
              )}
          </Grid>
        </div>

        <CTAMain type={getCtaMainType(eventCategory)} />

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          search={search}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredEvents}
          resultsBlockContent={filteredEvents.map(({ node: filteredEvent }, index) => {
            const eventDay = dayjs(filteredEvent.date).format('DD')
            const eventDayAppendix = getDateDayAppendix(eventDay)
            return filteredEvent.hippoVideoId ? (
              <VideoComponent
                key={`${debouncedSearch}-${filteredEvent.slug}-${index}`}
                additionalJsx={
                  <div className="strong-body-2 gray-text">
                    <Link
                      className="opacity-on-hover"
                      href={`/resources/events/${filteredEvent.isPast ? '#past-events/' : ''}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      Past {filteredEvent.type}
                    </Link>
                    <VerticalTextDivider /> {dayjs(filteredEvent.date).format('MMM')} {eventDay}
                    {eventDayAppendix} {dayjs(filteredEvent.date).format('YYYY')}
                  </div>
                }
                video={{
                  title: filteredEvent.cardTitle,
                  desc: filteredEvent.cardDescription,
                  gatsbyImageSrc: filteredEvent.banner,
                  imgWithBorder: filteredEvent.imageWithBorder,
                  hippoVideoId: filteredEvent.hippoVideoId,
                  showTitle: true,
                  hippoLink: !filteredEvent.withoutLandingPage ? `/resources/events/${filteredEvent.slug}/` : undefined,
                  hippoLinkText: !filteredEvent.withoutLandingPage ? 'Learn more' : undefined,
                  linkText: !filteredEvent.withoutLandingPage ? 'Learn more' : 'Watch video',
                  arrowLinkUrl: !filteredEvent.withoutLandingPage
                    ? `/resources/events/${filteredEvent.slug}/`
                    : undefined,
                }}
                withPlayButton
                withImageOverlay
                cssStyles={filteredTextCardStyle}
              />
            ) : (
              <TextCard
                key={`${debouncedSearch}-${filteredEvent.slug}-${index}`}
                additionalJsx={
                  <div className="strong-body-2 gray-text">
                    <Link
                      className="opacity-on-hover"
                      href={`/resources/events/${filteredEvent.isPast ? '#past-events/' : ''}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {filteredEvent.isPast && 'Past'} {filteredEvent.type}
                    </Link>
                    <VerticalTextDivider /> {dayjs(filteredEvent.date).format('MMM')} {eventDay}
                    {eventDayAppendix} {dayjs(filteredEvent.date).format('YYYY')}
                  </div>
                }
                title={filteredEvent.cardTitle}
                description={filteredEvent.cardDescription}
                gatsbyImageSrc={filteredEvent.banner}
                imgWithBorder={filteredEvent.imageWithBorder}
                linkText="Learn more"
                linkUrl={`/resources/events/${filteredEvent.slug}/`}
                cssStyles={filteredTextCardStyle}
              />
            )
          })}
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default Event

export const query = graphql`
  query ($id: String!) {
    event: contentfulEvent(id: { eq: $id }) {
      id
      slug
      title
      description {
        description
      }
      cardTitle
      cardDescription
      type
      fullDate
      hubspotFormId
      externalRegistrationLink
      imageWithBorder
      hippoVideoId
      isPast
      eventCategory
      eventInfoItems

      eventSpeakers {
        avatar {
          gatsbyImageData(width: 128, quality: 90)
        }
        name
        position
        description {
          description
        }
        linkedIn
      }

      airfolks {
        firstName
        role
        businessPicture {
          gatsbyImageData(width: 128, quality: 90)
        }
        linkedIn
      }

      banner {
        gatsbyImageData(width: 600, quality: 90)
        file {
          url
        }
      }

      heroImage {
        gatsbyImageData(width: 600, quality: 90)
      }

      eventSummary {
        raw
        references {
          ... on ContentfulCrossSellingEntry {
            id
            type
            ebookResource {
              slug
            }
            contentful_id
            __typename
            title
          }
          ... on Node {
            ... on ContentfulImageWithLink {
              id
              contentful_id
              __typename
              link
              image {
                title
                gatsbyImageData(width: 800, quality: 90)
              }
              imageDescription: childContentfulImageWithLinkDescriptionTextNode {
                description
              }
            }
          }
        }
      }
    }

    pastEventsData: allContentfulEvent(
      filter: { type: { nin: ["Conference", "Meetup"] }, isPast: { eq: true } }
      sort: { order: DESC, fields: date }
    ) {
      edges {
        node {
          id
          slug
          isPast
          eventCategory
          cardTitle
          cardDescription
          type
          date
          shortDate
          withoutLandingPage
          hippoVideoId
          banner {
            gatsbyImageData(width: 400, quality: 90)
          }
          imageWithBorder
        }
      }
    }

    upcomingEventsData: allContentfulEvent(filter: { isPast: { ne: true } }, sort: { order: ASC, fields: date }) {
      edges {
        node {
          id
          slug
          isPast
          cardTitle
          cardDescription
          type
          shortDate
          externalRegistrationLink
          banner {
            gatsbyImageData(width: 400, quality: 90)
          }
          imageWithBorder
        }
      }
    }
  }
`

const pastHeroStyle = (imageWithBorder?: boolean) => css`
  position: relative;
  background-color: #ade6ff38;
  ${imageWithBorder && 'border-bottom: 1px solid #efeff0'};
`

const hippoVideoStyle = css`
  max-width: 60rem;
  margin: 0 auto;
`

const upcomingDateStyle = css`
  margin: 0 0 0.5rem !important;
`

const upcomingHeroStyle = (isConferencePage: boolean) => css`
  margin: 4rem 0;

  ${isConferencePage
    ? `
    @media screen and (max-width: ${breakpoints.large}px) {
      text-align: center;
      row-gap: 3rem;

      .heading2 {
        max-width: 45rem;
        margin: auto !important;
      }
        
      .subtitle {
        max-width: 39rem;
        margin-left: auto !important;
        margin-right: auto !important;
      }

      .button-block {
        width: fit-content;

        @media screen and (max-width: ${breakpoints.small}px) {
          width: 100%;
        }
      }
    }
  `
    : `
  .heading2 {
    margin: 2.5rem 0 !important;
  }

  .hs-form {
    margin: 0;
  }

  .submitted-message {
    margin-top: 7rem !important;
  }

  .actions > input {
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    text-align: center;

    ul {
      text-align: left;
      width: fit-content;
      margin: 1.5rem auto 0.5rem;
    }

    .hs-form {
      margin: auto;
    }
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 2rem;

    .hs-form {
      gap: 0;
    }

    #firstname-5e7ce3ed-bf8b-4b83-84d9-1c27abaa7e87 {
      margin-top: 0;
    }

    .legal-consent-container {
      margin: 0.5rem 0.1rem 1rem;
    }

    input {
      width: 100%;
    }

    .submitted-message {
      margin-top: 4.5rem !important;
    }
  }
  `}
`

const heroImageStyle = css`
  @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${breakpoints.large}px) {
    width: 30rem;
    margin: auto;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    width: 20rem;
    margin: auto;
  }
`

const pastIntroStyle = css`
  margin: 4rem auto;
  max-width: 50rem;
  text-align: center;

  a {
    display: inline-block;
  }

  .heading2 {
    margin: 0.5rem 0 1rem !important;
    text-align: center !important;
  }
`

const detailsBlockStyle = (isPast: boolean) => css`
  ${!isPast &&
  `
    @media screen and (max-width: ${breakpoints.medium}px) {
      row-gap: 0;
    }
  `}
`

const listStyle = css`
  list-style-type: disc;
  margin-block-start: 1rem;
  margin-block-end: 1rem;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 2rem;

  li::marker {
    color: #318eff;
  }
`

const sidebarStyle = (speakersHeight: number) => css`
  position: sticky;
  top: calc(-6.6rem - ${speakersHeight}px);
`

const upcomingSpeakerBlockStyle = (index: number) => css`
  margin-top: ${index === 0 ? '2rem' : '1.25rem'};

  // event speaker profile pic
  .gatsby-image-wrapper {
    width: 4rem;
    height: 4rem;
  }
`

const speakersContainerStyle = css`
  margin-bottom: 2.5rem !important;

  // event speaker profile pic
  .gatsby-image-wrapper {
    width: 8rem;
    height: 8rem;
  }
`

const speakerDescriptionStyle = css`
  display: flex;
  align-items: center;
  margin: 0 !important;
`

const speakerImageStyle = css`
  position: relative;
  width: fit-content;
  margin-bottom: 0.5rem;

  &:hover {
    .linked-in-icon {
      opacity: 0.65;
    }
  }
`

const linkedInIconStyle = (isPast: boolean, eventSummary: boolean) => css`
  ${isPast
    ? `
    position: absolute;
    bottom: 0;
    right: 0;
    width: ${eventSummary ? '1.5rem' : '2rem'};
    height: ${eventSummary ? '1.5rem' : '2rem'};
    transition: all 0.2s ease;
    ${
      eventSummary
        ? `
        & > .gatsby-image-wrapper {
          width: 1.5rem !important;
          height: 1.5rem !important; 
        }
        `
        : `
        // linkedIn icon
        & > .gatsby-image-wrapper {
          width: 2rem !important;
          height: 2rem !important; 
        }

        `
    };`
    : `
    // linkedIn icon
    & > .gatsby-image-wrapper {
      margin-top: 0.25rem;
      width: 1.5rem !important;
      height: 1.5rem !important; 
    }
    `};
`

const summarySpeakerImageStyle = css`
  width: 4rem;
  position: relative;

  &:hover {
    & > a {
      opacity: 0.65;
    }
  }
`

const moreWebinarsStyle = css`
  .gatsby-image-wrapper {
    // banner images have important information at bottom - needs to be visible
    aspect-ratio: 16/9;
  }
`
